import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Bolt, Download, ExpandMore, Message, Refresh } from '@mui/icons-material';
import moment from 'moment';
import getSimulationAgentMessages from 'redux/actions/Common/getSimulationAgentMessages';
import getSimulationAgentSummaries from 'redux/actions/Common/getSimulationAgentSummaries';
import forceSimulationAgentTick from 'redux/actions/Common/forceSimulationAgentTick';
import manualBuyerAction from 'redux/actions/Common/manualBuyerAction';
import PersonaEditor from './PersonaEditor';
import { isTerminalStatus } from './simulationUtils';

// The messages endpoint has no cursor and no server-side clamp on `limit`, so
// "everything" is just a very large limit.
export const FULL_DUMP_LIMIT = 100000;

const MESSAGE_LIMITS = [
  { value: 15, label: 'Last 15' },
  { value: 50, label: 'Last 50' },
  { value: 200, label: 'Last 200' },
  { value: FULL_DUMP_LIMIT, label: 'All' },
];

const SUMMARY_LIMIT = 5;

// After a forced tick, re-read the transcript once the agent has had time to
// finish its LLM turn.
const FORCED_TICK_REFRESH_MS = 10000;

export const downloadJson = (fileName, data) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const safeFileName = (value) =>
  String(value || 'agent')
    .replace(/[^a-z0-9-_]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();

// Promise wrapper so callers can fetch several agents' transcripts in turn.
export const fetchFullTranscript = (dispatch, simulationId, agentId) =>
  new Promise((resolve, reject) =>
    dispatch(
      getSimulationAgentMessages(
        simulationId,
        agentId,
        FULL_DUMP_LIMIT,
        resolve,
        (message) => reject(new Error(message)),
      ),
    ),
  );

const prettyTrace = (trace) => {
  if (!trace) return '';
  try {
    return JSON.stringify(JSON.parse(trace), null, 2);
  } catch (e) {
    return trace;
  }
};

// Messages come back as flat records, two per tick (agent_input then
// llm_response), oldest first — group them so each tick renders as one block.
const groupByTick = (messages) => {
  const groups = [];
  messages.forEach((message) => {
    const last = groups[groups.length - 1];
    if (last && last.tickNumber === message?.tickNumber) {
      last.messages.push(message);
    } else {
      groups.push({ tickNumber: message?.tickNumber, messages: [message] });
    }
  });
  return groups;
};

const AgentTranscript = ({
  simulationId,
  agent,
  simulationStatus,
  persona,
  onPersonaSaved,
  onNotify,
}) => {
  const dispatch = useDispatch();
  const agentId = agent?.agentConversationId;

  const [messages, setMessages] = useState([]);
  const [fetchingMessages, setFetchingMessages] = useState(false);
  const [messagesError, setMessagesError] = useState(null);
  const [limit, setLimit] = useState(15);
  const [showPrompts, setShowPrompts] = useState(false);

  const [summaries, setSummaries] = useState([]);
  const [fetchingSummaries, setFetchingSummaries] = useState(false);
  const [summariesError, setSummariesError] = useState(null);
  const [allSummaries, setAllSummaries] = useState(false);

  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const [forcingTick, setForcingTick] = useState(false);
  const forcedRefreshTimer = useRef(null);

  const [manualActionOpen, setManualActionOpen] = useState(false);
  const [manualActionSubject, setManualActionSubject] = useState('');
  const [manualActionBody, setManualActionBody] = useState('');
  const [submittingManualAction, setSubmittingManualAction] = useState(false);
  const [manualActionError, setManualActionError] = useState(null);

  // Drops responses for an agent/limit the user has already moved away from.
  const messagesSeq = useRef(0);
  const summariesSeq = useRef(0);

  const fetchMessages = (nextLimit = limit) => {
    if (!agentId) return;
    const seq = ++messagesSeq.current;
    setFetchingMessages(true);
    setMessagesError(null);
    dispatch(
      getSimulationAgentMessages(
        simulationId,
        agentId,
        nextLimit,
        (data) => {
          if (seq !== messagesSeq.current) return;
          setMessages(data || []);
          setFetchingMessages(false);
        },
        (message) => {
          if (seq !== messagesSeq.current) return;
          setMessagesError(message);
          setFetchingMessages(false);
        },
      ),
    );
  };

  const fetchSummaries = (showAll = allSummaries) => {
    if (!agentId) return;
    const seq = ++summariesSeq.current;
    setFetchingSummaries(true);
    setSummariesError(null);
    dispatch(
      getSimulationAgentSummaries(
        simulationId,
        agentId,
        showAll ? FULL_DUMP_LIMIT : SUMMARY_LIMIT,
        (data) => {
          if (seq !== summariesSeq.current) return;
          setSummaries(data || []);
          setFetchingSummaries(false);
        },
        (message) => {
          if (seq !== summariesSeq.current) return;
          setSummariesError(message);
          setFetchingSummaries(false);
        },
      ),
    );
  };

  useEffect(() => {
    setMessages([]);
    setSummaries([]);
    setAllSummaries(false);
    setDownloadError(null);
    fetchMessages(limit);
    fetchSummaries(false);
    return () => clearTimeout(forcedRefreshTimer.current);
  }, [simulationId, agentId]);

  const handleForceTick = () => {
    if (forcingTick) return;
    setForcingTick(true);
    dispatch(
      forceSimulationAgentTick(
        simulationId,
        agentId,
        (data) => {
          setForcingTick(false);
          if (data?.forced) {
            onNotify({
              severity: 'success',
              message: `Tick requested for ${agent?.name ||
                'agent'} — the transcript refreshes in a few seconds.`,
            });
            clearTimeout(forcedRefreshTimer.current);
            forcedRefreshTimer.current = setTimeout(() => {
              fetchMessages();
              fetchSummaries();
            }, FORCED_TICK_REFRESH_MS);
          } else {
            // forced: false is a normal outcome (not running / already
            // pending), never an error.
            onNotify({
              severity: 'info',
              message: data?.reason || 'Tick was not forced',
            });
          }
        },
        (message) => {
          setForcingTick(false);
          onNotify({ severity: 'error', message });
        },
      ),
    );
  };

  const handleOpenManualAction = () => {
    setManualActionSubject('');
    setManualActionBody('');
    setManualActionError(null);
    setManualActionOpen(true);
  };

  const handleCloseManualAction = () => {
    if (submittingManualAction) return;
    setManualActionOpen(false);
  };

  const handleSubmitManualAction = () => {
    if (submittingManualAction || !manualActionBody.trim()) return;
    setSubmittingManualAction(true);
    setManualActionError(null);
    dispatch(
      manualBuyerAction(
        simulationId,
        agentId,
        { subject: manualActionSubject.trim(), body: manualActionBody.trim() },
        () => {
          setSubmittingManualAction(false);
          setManualActionOpen(false);
          onNotify({
            severity: 'success',
            message: `Manual message logged for ${agent?.name ||
              'buyer'} — the seller discovers it on its next tick.`,
          });
          fetchMessages();
        },
        (message) => {
          setSubmittingManualAction(false);
          setManualActionError(message);
        },
      ),
    );
  };

  const handleDownload = async () => {
    setDownloading(true);
    setDownloadError(null);
    try {
      const data = await fetchFullTranscript(dispatch, simulationId, agentId);
      downloadJson(
        `simulation-${safeFileName(simulationId)}-${safeFileName(
          agent?.name,
        )}-transcript.json`,
        {
          simulationId,
          agent,
          exportedAt: new Date().toISOString(),
          messageCount: (data || []).length,
          messages: data || [],
        },
      );
    } catch (e) {
      setDownloadError(e?.message || 'Failed to download transcript');
    }
    setDownloading(false);
  };

  const visibleMessages = showPrompts
    ? messages
    : messages.filter((message) => message?.role !== 'agent_input');
  const tickGroups = groupByTick(visibleMessages);

  return (
    <Box display={'flex'} flexDirection={'column'} gap={2}>
      <Box
        display={'flex'}
        alignItems={'center'}
        justifyContent={'space-between'}
        flexWrap={'wrap'}
        gap={1}
      >
        <Box>
          <Box display={'flex'} alignItems={'center'} gap={1}>
            <Typography variant={'subtitle1'} fontWeight={'bold'}>
              {agent?.name || '--'}
            </Typography>
            <Chip
              size="small"
              label={agent?.role || 'agent'}
              color={agent?.role === 'seller' ? 'primary' : 'default'}
              variant="outlined"
            />
          </Box>
          <Typography variant={'caption'} color={'text.secondary'}>
            {agent?.email}
          </Typography>
        </Box>
        <Box display={'flex'} alignItems={'center'} gap={1}>
          <Tooltip
            title={
              simulationStatus === 'running'
                ? 'Make this agent take its next turn now'
                : 'Only available while the simulation is running'
            }
          >
            <span>
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                onClick={handleForceTick}
                disabled={simulationStatus !== 'running' || forcingTick}
                startIcon={
                  forcingTick ? (
                    <CircularProgress size={14} />
                  ) : (
                    <Bolt fontSize="small" />
                  )
                }
              >
                Force tick
              </Button>
            </span>
          </Tooltip>
          {agent?.role === 'buyer' ? (
            <Tooltip title="Log a conversation on this buyer's behalf, bypassing the LLM entirely">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleOpenManualAction}
                  startIcon={<Message fontSize="small" />}
                >
                  Log manual message
                </Button>
              </span>
            </Tooltip>
          ) : null}
          <Tooltip title="Downloads every message of this agent as JSON">
            <span>
              <Button
                size="small"
                variant="outlined"
                onClick={handleDownload}
                disabled={downloading}
                startIcon={
                  downloading ? (
                    <CircularProgress size={14} />
                  ) : (
                    <Download fontSize="small" />
                  )
                }
              >
                Download full transcript
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>
      {downloadError ? <Alert severity="error">{downloadError}</Alert> : null}

      <Dialog open={manualActionOpen} onClose={handleCloseManualAction} fullWidth maxWidth="sm">
        <DialogTitle>Log manual message for {agent?.name || 'buyer'}</DialogTitle>
        <DialogContent>
          <Box display={'flex'} flexDirection={'column'} gap={2} pt={1}>
            <Typography variant={'body2'} color={'text.secondary'}>
              Creates a real interaction on this deal as if this buyer sent it, and posts it to
              the conversation channel so the seller's next tick discovers it — no LLM call is
              made.
            </Typography>
            {manualActionError ? (
              <Alert severity="error">{manualActionError}</Alert>
            ) : null}
            <TextField
              size="small"
              label="Subject"
              value={manualActionSubject}
              onChange={(e) => setManualActionSubject(e.target.value)}
              disabled={submittingManualAction}
            />
            <TextField
              size="small"
              label="Message *"
              multiline
              minRows={4}
              value={manualActionBody}
              onChange={(e) => setManualActionBody(e.target.value)}
              disabled={submittingManualAction}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseManualAction} disabled={submittingManualAction}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitManualAction}
            disabled={submittingManualAction || !manualActionBody.trim()}
            startIcon={
              submittingManualAction ? <CircularProgress size={14} /> : null
            }
          >
            Log message
          </Button>
        </DialogActions>
      </Dialog>

      <PersonaEditor
        simulationId={simulationId}
        agent={agent}
        persona={persona}
        disabled={isTerminalStatus(simulationStatus)}
        onSaved={(id, personaPrompt) => {
          onPersonaSaved(id, personaPrompt);
          onNotify({
            severity: 'success',
            message: `Persona updated for ${agent?.name ||
              'agent'} — applies from its next tick.`,
          });
        }}
        onError={(message) => onNotify({ severity: 'error', message })}
      />

      <Accordion disableGutters variant="outlined">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display={'flex'} alignItems={'center'} gap={1}>
            <Typography variant={'subtitle2'} fontWeight={'bold'}>
              Summaries
            </Typography>
            {fetchingSummaries ? (
              <CircularProgress size={14} />
            ) : (
              <Chip size="small" label={summaries.length} />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {summariesError ? (
            <Alert severity="error">{summariesError}</Alert>
          ) : summaries.length === 0 && !fetchingSummaries ? (
            <Typography variant={'body2'} color={'text.secondary'}>
              No summaries yet — they are produced automatically once this
              agent’s history grows long.
            </Typography>
          ) : (
            <Box display={'flex'} flexDirection={'column'} gap={1.5}>
              {summaries.map((summary) => (
                <Box key={summary?.id}>
                  <Typography variant={'caption'} color={'text.secondary'}>
                    Messages {summary?.coveredMessageRangeStart}–
                    {summary?.coveredMessageRangeEnd}
                    {summary?.createdOn
                      ? ` · ${moment(summary.createdOn).format(
                          'DD MMM YYYY, hh:mm A',
                        )}`
                      : ''}
                  </Typography>
                  <Typography variant={'body2'} sx={{ whiteSpace: 'pre-wrap' }}>
                    {summary?.summaryText}
                  </Typography>
                </Box>
              ))}
              {!allSummaries && summaries.length >= SUMMARY_LIMIT ? (
                <Box>
                  <Button
                    size="small"
                    onClick={() => {
                      setAllSummaries(true);
                      fetchSummaries(true);
                    }}
                  >
                    Show all summaries
                  </Button>
                </Box>
              ) : null}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      <Box
        display={'flex'}
        alignItems={'center'}
        justifyContent={'space-between'}
        flexWrap={'wrap'}
        gap={1}
      >
        <Typography variant={'subtitle2'} fontWeight={'bold'}>
          Transcript
        </Typography>
        <Box display={'flex'} alignItems={'center'} gap={1}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showPrompts}
                onChange={(e) => setShowPrompts(e.target.checked)}
              />
            }
            label={<Typography variant={'body2'}>Show tick prompts</Typography>}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="message-limit-label">Show</InputLabel>
            <Select
              labelId="message-limit-label"
              label="Show"
              value={limit}
              onChange={(e) => {
                setLimit(e.target.value);
                fetchMessages(e.target.value);
              }}
            >
              {MESSAGE_LIMITS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              fetchMessages();
              fetchSummaries();
            }}
            disabled={fetchingMessages}
            startIcon={<Refresh fontSize="small" />}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {messagesError ? (
        <Alert severity="error">{messagesError}</Alert>
      ) : fetchingMessages ? (
        <Box
          display={'flex'}
          alignItems={'center'}
          justifyContent={'center'}
          gap={1}
          py={4}
        >
          <CircularProgress size={20} />
          <Typography variant={'body2'} color={'text.secondary'}>
            Loading messages...
          </Typography>
        </Box>
      ) : tickGroups.length === 0 ? (
        <Typography
          variant={'body2'}
          color={'text.secondary'}
          textAlign={'center'}
          py={4}
        >
          No messages yet.
        </Typography>
      ) : (
        <Box display={'flex'} flexDirection={'column'} gap={1.5}>
          <Typography variant={'caption'} color={'text.secondary'}>
            Showing {messages.length} most recent message
            {messages.length === 1 ? '' : 's'}, oldest first.
          </Typography>
          {tickGroups.map((group, groupIndex) => (
            <Box key={`${group.tickNumber}-${groupIndex}`}>
              <Divider textAlign="left" sx={{ mb: 1 }}>
                <Typography variant={'caption'} color={'text.secondary'}>
                  Tick #{group.tickNumber ?? '--'}
                </Typography>
              </Divider>
              {group.messages.map((message) =>
                message?.role === 'agent_input' ? (
                  <Typography
                    key={message?.id}
                    variant={'caption'}
                    color={'text.secondary'}
                    display={'block'}
                    sx={{
                      whiteSpace: 'pre-wrap',
                      fontStyle: 'italic',
                      mb: 1,
                    }}
                  >
                    System: {message?.content}
                  </Typography>
                ) : message?.role === 'tick_error' ? (
                  // Requirement 2, Phase 5 — before this, a failed tick persisted nothing at
                  // all here; it was only ever visible in ai-service's own stdout logs.
                  <Alert key={message?.id} severity="error" sx={{ mb: 1 }}>
                    <Typography
                      variant={'body2'}
                      sx={{ whiteSpace: 'pre-wrap' }}
                    >
                      Tick failed: {message?.content}
                    </Typography>
                  </Alert>
                ) : message?.role === 'tick_skipped' ? (
                  <Alert key={message?.id} severity="warning" sx={{ mb: 1 }}>
                    <Typography variant={'body2'}>
                      {message?.content}
                    </Typography>
                  </Alert>
                ) : (
                  <Paper
                    key={message?.id}
                    variant="outlined"
                    sx={{ p: 1.5, mb: 1 }}
                  >
                    <Box
                      display={'flex'}
                      justifyContent={'space-between'}
                      gap={1}
                      mb={0.5}
                    >
                      <Typography variant={'caption'} fontWeight={'bold'}>
                        {agent?.name}
                      </Typography>
                      <Typography variant={'caption'} color={'text.secondary'}>
                        {message?.createdOn
                          ? moment(message.createdOn).format(
                              'DD MMM YYYY, hh:mm:ss A',
                            )
                          : ''}
                      </Typography>
                    </Box>
                    <Typography
                      variant={'body2'}
                      sx={{ whiteSpace: 'pre-wrap' }}
                    >
                      {message?.llmRes || message?.content || '--'}
                    </Typography>
                    {message?.thinkingAndToolCallChainReqRes ? (
                      <Accordion
                        disableGutters
                        elevation={0}
                        // Traces are large — only render one once expanded.
                        TransitionProps={{ unmountOnExit: true }}
                        sx={{
                          mt: 1,
                          '&:before': { display: 'none' },
                          bgcolor: 'transparent',
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMore fontSize="small" />}
                          sx={{ px: 0, minHeight: 0 }}
                        >
                          <Typography variant={'caption'} color={'primary'}>
                            Show reasoning & tool calls
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 0 }}>
                          <Box
                            component="pre"
                            sx={{
                              m: 0,
                              p: 1,
                              maxHeight: 400,
                              overflow: 'auto',
                              fontSize: 12,
                              fontFamily: 'monospace',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              bgcolor: 'action.hover',
                              borderRadius: 1,
                            }}
                          >
                            {prettyTrace(
                              message.thinkingAndToolCallChainReqRes,
                            )}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    ) : null}
                  </Paper>
                ),
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default AgentTranscript;
